import { useState } from 'react';
import { Link } from 'react-router-dom';
import { leadsApi } from '../utils/api';

// Benefits for schools
const benefits = [
  {
    icon: '📊',
    title: 'Progress Tracking',
    description: 'Detailed reports aligned with IEP objectives. Track student progress and share with parents and administrators.',
  },
  {
    icon: '👥',
    title: 'Unlimited Students',
    description: 'Site licenses provide access for all students who need it. No per-student fees eating into your budget.',
  },
  {
    icon: '🎯',
    title: 'IEP Goal Alignment',
    description: 'Games map directly to common speech and communication IEP goals. Easy documentation for compliance.',
  },
  {
    icon: '📱',
    title: 'Multi-Device Support',
    description: 'Works on tablets, Chromebooks, and computers. Students can practice in school or at home.',
  },
  {
    icon: '🔐',
    title: 'FERPA Compliant',
    description: 'Student data is protected. We take privacy seriously with enterprise-grade security.',
  },
  {
    icon: '📚',
    title: 'Training & Support',
    description: 'Professional development for staff included. Dedicated support for implementation.',
  },
];

// Use cases
const useCases = [
  {
    title: 'Speech Therapy Sessions',
    description: 'SLPs use games during sessions for engaging, measurable practice.',
    icon: '🗣️',
    color: 'from-teal-400 to-teal-500',
  },
  {
    title: 'Special Education Classrooms',
    description: 'Teachers integrate games into daily routines for consistent practice.',
    icon: '🏫',
    color: 'from-coral-400 to-coral-500',
  },
  {
    title: 'Resource Rooms',
    description: 'Para-professionals facilitate practice with clear, structured games.',
    icon: '📖',
    color: 'from-navy-500 to-navy-600',
  },
  {
    title: 'Home Carryover',
    description: 'Parents continue practice at home with the same tools used at school.',
    icon: '🏠',
    color: 'from-lavender-400 to-lavender-500',
  },
];

// Pricing tiers for schools
const schoolPlans = [
  {
    name: 'School',
    description: 'Perfect for individual schools',
    price: '299',
    period: '/month',
    features: [
      'Up to 50 student accounts',
      'All games and content',
      'Progress tracking dashboard',
      'IEP goal mapping',
      'Email support',
      'Staff training webinar',
    ],
    cta: 'Contact Sales',
    popular: false,
  },
  {
    name: 'District',
    description: 'For school districts',
    price: 'Custom',
    period: 'pricing',
    features: [
      'Unlimited student accounts',
      'All games and content',
      'District-wide analytics',
      'SSO integration',
      'Priority support',
      'On-site training available',
      'Custom reporting',
      'API access',
    ],
    cta: 'Get a Quote',
    popular: true,
  },
  {
    name: 'State/Regional',
    description: 'For state programs & ESCs',
    price: 'Custom',
    period: 'pricing',
    features: [
      'All District features',
      'Multi-district management',
      'State-level reporting',
      'Dedicated account manager',
      'Custom game development',
      'White-label options',
    ],
    cta: 'Contact Us',
    popular: false,
  },
];

// What the programme is built on. These describe the clinical method
// (docs/GAME-LEVEL-ENGINE.md) - not adoption, which we do not claim.
const foundations = [
  {
    icon: '🧠',
    title: 'ABA-grounded',
    detail: 'Errorless learning, prompt fading and manding',
  },
  {
    icon: '🪜',
    title: '10-phase framework',
    detail: 'Single words through to full conversation',
  },
  {
    icon: '🩺',
    title: '40+ years',
    detail: "Dr. Terri Smith's clinical psychology practice",
  },
  {
    icon: '🎙️',
    title: 'Voice-controlled',
    detail: "The child's own speech drives the game",
  },
];

// Testimonials
const testimonials = [
  {
    quote: "Our speech therapists love it. The students are actually excited for their sessions now. The progress tracking makes documentation so much easier.",
    author: "Jennifer Martinez",
    role: "Director of Special Education",
    school: "Lincoln Unified School District",
    avatar: "👩‍💼",
  },
  {
    quote: "Implementation was smooth. The training was excellent and support has been responsive. Best investment we've made for our special ed program.",
    author: "Robert Thompson",
    role: "Principal",
    school: "Oak Grove Elementary",
    avatar: "👨‍💼",
  },
];

export default function ForSchools() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    district: '',
    role: '',
    students: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await leadsApi.submit({ source: 'schools', ...formData });
      setSubmitted(true);
    } catch (error) {
      // Show the failure and keep the form filled in - never confirm a
      // request the server did not store (B8).
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-coral-600 via-coral-500 to-coral-400 overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-96 h-96 bg-navy-800/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 py-20 relative z-10">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-8 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="inline-block px-4 py-2 bg-white/20 text-white font-heading font-semibold rounded-full text-sm mb-6">
                🏫 For Schools & Districts
              </span>
              <h1 className="font-display text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Transform Your <br />
                <span className="text-navy-900">Special Education Program</span>
              </h1>
              <p className="text-xl text-coral-100 font-body mb-8 max-w-lg">
                Give your students access to voice-controlled games that make speech practice 
                engaging and measurable. Designed for schools with IEP alignment built in.
              </p>

              {/* What the method is built on */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {foundations.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-white/10 rounded-2xl p-4">
                    <span className="text-2xl leading-none" aria-hidden="true">{item.icon}</span>
                    <div>
                      <div className="font-heading font-bold text-white">{item.title}</div>
                      <div className="text-coral-100 text-sm leading-snug">{item.detail}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact"
                  className="inline-flex items-center px-8 py-4 bg-white text-coral-600 font-heading font-bold text-lg rounded-2xl transition-all hover:scale-105 shadow-lg"
                >
                  Request a Demo
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </a>
                <a
                  href="#pricing"
                  className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white/50 text-white font-heading font-semibold text-lg rounded-2xl transition-all hover:bg-white/10"
                >
                  View Pricing
                </a>
              </div>
            </div>

            {/* Visual */}
            <div className="hidden lg:block">
              <div className="bg-white rounded-3xl p-8 shadow-2xl">
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src="https://storage.googleapis.com/msgsndr/aAuxGGoSZCGAlUv23ouM/media/696ab3312ed1fbc29124b9f0.png" 
                    alt="SpeechFormation" 
                    className="h-8 w-auto"
                  />
                  <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-bold rounded-full">
                    School Edition
                  </span>
                </div>

                {/* Mock dashboard */}
                <div className="space-y-4">
                  <div className="bg-navy-50 rounded-2xl p-4">
                    <div className="flex justify-between items-center mb-3">
                      <span className="font-heading font-bold text-navy-800">Class Progress</span>
                      <span className="text-teal-600 text-sm font-medium">This Week</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-coral-100 rounded-full flex items-center justify-center text-sm">👧</div>
                        <div className="flex-1 h-3 bg-navy-100 rounded-full overflow-hidden">
                          <div className="h-full bg-teal-500 rounded-full" style={{ width: '85%' }} />
                        </div>
                        <span className="text-sm text-navy-600 font-medium">85%</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-sm">👦</div>
                        <div className="flex-1 h-3 bg-navy-100 rounded-full overflow-hidden">
                          <div className="h-full bg-coral-500 rounded-full" style={{ width: '72%' }} />
                        </div>
                        <span className="text-sm text-navy-600 font-medium">72%</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-sunshine-100 rounded-full flex items-center justify-center text-sm">👧</div>
                        <div className="flex-1 h-3 bg-navy-100 rounded-full overflow-hidden">
                          <div className="h-full bg-sunshine-500 rounded-full" style={{ width: '91%' }} />
                        </div>
                        <span className="text-sm text-navy-600 font-medium">91%</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-teal-50 rounded-xl p-3 text-center">
                      <div className="text-2xl mb-1">🎮</div>
                      <div className="font-display text-xl font-bold text-teal-600">342</div>
                      <div className="text-xs text-navy-500">Games Played</div>
                    </div>
                    <div className="bg-coral-50 rounded-xl p-3 text-center">
                      <div className="text-2xl mb-1">⏱️</div>
                      <div className="font-display text-xl font-bold text-coral-500">18h</div>
                      <div className="text-xs text-navy-500">Practice Time</div>
                    </div>
                    <div className="bg-navy-50 rounded-xl p-3 text-center">
                      <div className="text-2xl mb-1">🎯</div>
                      <div className="font-display text-xl font-bold text-navy-600">12</div>
                      <div className="text-xs text-navy-500">Goals Met</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-coral-100 text-coral-700 font-heading font-semibold rounded-full text-sm mb-4">
              ✨ Why Schools Choose Us
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-800 mb-4">
              Built for <span className="text-teal-500">Education</span>
            </h2>
            <p className="text-navy-600 font-body text-lg max-w-2xl mx-auto">
              We understand the unique needs of schools. That's why we've built features specifically for special education programs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-coral-50 to-white p-8 rounded-3xl border border-coral-100 hover:shadow-card-hover transition-all hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-coral-500 rounded-2xl flex items-center justify-center text-2xl mb-5 shadow-coral-glow">
                  {benefit.icon}
                </div>
                <h3 className="font-heading font-bold text-xl text-navy-800 mb-3">{benefit.title}</h3>
                <p className="text-navy-600 font-body">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gradient-to-br from-navy-50 to-coral-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-navy-100 text-navy-700 font-heading font-semibold rounded-full text-sm mb-4">
              🎯 Use Cases
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-800 mb-4">
              How Schools <span className="text-coral-500">Use SpeechFormation</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover transition-all">
                <div className={`h-32 bg-gradient-to-br ${useCase.color} flex items-center justify-center`}>
                  <span className="text-5xl">{useCase.icon}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-lg text-navy-800 mb-2">{useCase.title}</h3>
                  <p className="text-navy-600 font-body text-sm">{useCase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 font-heading font-semibold rounded-full text-sm mb-4">
              💰 School Pricing
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-800 mb-4">
              Plans That Scale <span className="text-coral-500">With You</span>
            </h2>
            <p className="text-navy-600 font-body text-lg max-w-2xl mx-auto">
              From individual schools to statewide programs, we have a plan that fits.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {schoolPlans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-3xl p-8 shadow-card hover:shadow-card-hover transition-all ${
                  plan.popular ? 'border-4 border-coral-500 scale-105' : 'border border-navy-100'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-coral-500 text-white text-sm font-bold rounded-full">
                    Most Popular
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="font-heading font-bold text-xl text-navy-800 mb-2">{plan.name}</h3>
                  <p className="text-navy-500 text-sm mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    {plan.price === 'Custom' ? (
                      <span className="text-3xl font-display font-bold text-navy-800">{plan.price}</span>
                    ) : (
                      <>
                        <span className="text-navy-400">$</span>
                        <span className="text-4xl font-display font-bold text-navy-800">{plan.price}</span>
                      </>
                    )}
                    <span className="text-navy-500">{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-navy-600 font-body text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  className={`block w-full text-center py-3 rounded-xl font-heading font-semibold transition-all ${
                    plan.popular
                      ? 'bg-coral-500 hover:bg-coral-600 text-white shadow-button'
                      : 'bg-navy-100 hover:bg-navy-200 text-navy-800'
                  }`}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>

          <p className="text-center text-navy-500 mt-8 font-body">
            All plans include a free pilot period. Volume discounts available for large districts.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-navy-800 to-navy-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-white/10 text-coral-300 font-heading font-semibold rounded-full text-sm mb-4">
              💬 Success Stories
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Schools Love <span className="text-teal-400">SpeechFormation</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-sunshine-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-white/90 font-body text-lg mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-coral-500 rounded-full flex items-center justify-center text-xl">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-heading font-bold text-white">{testimonial.author}</div>
                    <div className="text-coral-300 text-sm">{testimonial.role}</div>
                    <div className="text-white/50 text-xs">{testimonial.school}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-coral-100 text-coral-700 font-heading font-semibold rounded-full text-sm mb-4">
                📬 Get Started
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-navy-800 mb-4">
                Request a <span className="text-teal-500">Demo</span>
              </h2>
              <p className="text-navy-600 font-body text-lg">
                See how SpeechFormation can transform your special education program.
              </p>
            </div>

            {submitted ? (
              <div className="text-center py-12 bg-gradient-to-br from-coral-50 to-teal-50 rounded-3xl">
                <div className="w-20 h-20 bg-coral-500 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">
                  ✓
                </div>
                <h3 className="font-heading font-bold text-2xl text-navy-800 mb-4">Request Received!</h3>
                <p className="text-navy-600 font-body max-w-md mx-auto">
                  Thank you for your interest! Our education team will reach out within 24 hours to schedule your demo.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-gradient-to-br from-navy-50 to-coral-50 rounded-3xl p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-coral-500 focus:outline-none transition-colors font-body bg-white"
                      placeholder="Jane Smith"
                    />
                  </div>
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-coral-500 focus:outline-none transition-colors font-body bg-white"
                      placeholder="jane@school.edu"
                    />
                  </div>
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-coral-500 focus:outline-none transition-colors font-body bg-white"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Your Role *</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-coral-500 focus:outline-none transition-colors font-body bg-white"
                    >
                      <option value="">Select...</option>
                      <option value="special_ed_director">Director of Special Education</option>
                      <option value="principal">Principal</option>
                      <option value="slp">Speech-Language Pathologist</option>
                      <option value="teacher">Special Education Teacher</option>
                      <option value="administrator">District Administrator</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">School Name *</label>
                    <input
                      type="text"
                      name="school"
                      value={formData.school}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-coral-500 focus:outline-none transition-colors font-body bg-white"
                      placeholder="Oak Grove Elementary"
                    />
                  </div>
                  <div>
                    <label className="block font-heading font-semibold text-navy-800 mb-2">District (if applicable)</label>
                    <input
                      type="text"
                      name="district"
                      value={formData.district}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-coral-500 focus:outline-none transition-colors font-body bg-white"
                      placeholder="Lincoln Unified School District"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Estimated Students Who Would Use</label>
                    <select
                      name="students"
                      value={formData.students}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-coral-500 focus:outline-none transition-colors font-body bg-white"
                    >
                      <option value="">Select...</option>
                      <option value="1-25">1-25 students</option>
                      <option value="26-50">26-50 students</option>
                      <option value="51-100">51-100 students</option>
                      <option value="100-500">100-500 students</option>
                      <option value="500+">500+ students</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block font-heading font-semibold text-navy-800 mb-2">Additional Information</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 border-2 border-navy-100 rounded-xl focus:border-coral-500 focus:outline-none transition-colors font-body bg-white resize-none"
                      placeholder="Tell us about your current program and what you're hoping to achieve..."
                    />
                  </div>
                </div>

                {submitError && (
                  <div
                    role="alert"
                    className="mt-8 flex items-start gap-3 rounded-2xl border-2 border-red-200 bg-red-50 p-4"
                  >
                    <span className="text-xl leading-none" aria-hidden="true">⚠️</span>
                    <div>
                      <p className="font-heading font-bold text-red-800">We couldn't send your request</p>
                      <p className="font-body text-red-700 text-sm mt-1">{submitError}</p>
                      <p className="font-body text-red-700 text-sm mt-1">
                        Your details are still here, so you can try again — or email us directly at{' '}
                        <a href="mailto:schools@speechformation.com" className="underline font-semibold">
                          schools@speechformation.com
                        </a>.
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-coral-500 hover:bg-coral-600 text-white font-heading font-bold text-lg rounded-2xl transition-all hover:scale-[1.02] shadow-coral-glow disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      'Request Demo'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 bg-gradient-to-r from-coral-500 to-coral-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
            Questions? We're Here to Help.
          </h2>
          <p className="text-coral-100 font-body text-lg mb-6">
            Our education specialists are ready to discuss your needs.
          </p>
          <a
            href="mailto:schools@speechformation.com"
            className="inline-flex items-center px-8 py-4 bg-white text-coral-600 font-heading font-bold rounded-2xl transition-all hover:scale-105"
          >
            Contact Education Team
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </a>
        </div>
      </section>
    </div>
  );
}
